/**
 * Enhanced fetch with retry logic, timeout handling, and exponential backoff
 * for handling poor network conditions gracefully.
 */

interface FetchWithRetryOptions extends RequestInit {
  timeout?: number;
  retries?: number;
  retryDelay?: number;
  onRetry?: (attempt: number, error: Error) => void;
}

const DEFAULT_TIMEOUT = 30000; // 30 seconds
const DEFAULT_RETRIES = 3;
const DEFAULT_RETRY_DELAY = 1000; // 1 second

/**
 * Fetch with timeout support
 */
async function fetchWithTimeout(
  url: string,
  options: RequestInit & { timeout?: number }
): Promise<Response> {
  const { timeout = DEFAULT_TIMEOUT, ...fetchOptions } = options;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    const response = await fetch(url, {
      ...fetchOptions,
      signal: controller.signal,
    });
    clearTimeout(timeoutId);
    return response;
  } catch (error: any) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error(`Request timeout after ${timeout}ms`);
    }
    throw error;
  }
}

/**
 * Check if error is retryable (network errors, timeouts, 5xx errors)
 */
function isRetryableError(error: any, response?: Response): boolean {
  // Network errors (no response)
  if (!response) {
    return true;
  }

  // Server errors (5xx) are retryable
  if (response.status >= 500) {
    return true;
  }

  // 429 Too Many Requests - should retry with backoff
  if (response.status === 429) {
    return true;
  }

  // Client errors (4xx) are not retryable
  return false;
}

/**
 * Calculate exponential backoff delay
 */
function getBackoffDelay(attempt: number, baseDelay: number): number {
  // Exponential backoff: baseDelay * 2^attempt with jitter
  const exponentialDelay = baseDelay * Math.pow(2, attempt);
  const jitter = Math.random() * 0.3 * exponentialDelay; // ±30% jitter
  return exponentialDelay + jitter;
}

/**
 * Enhanced fetch with automatic retry, timeout, and exponential backoff
 */
export async function fetchWithRetry(
  url: string,
  options: FetchWithRetryOptions = {}
): Promise<Response> {
  const {
    timeout = DEFAULT_TIMEOUT,
    retries = DEFAULT_RETRIES,
    retryDelay = DEFAULT_RETRY_DELAY,
    onRetry,
    ...fetchOptions
  } = options;

  let lastError: Error | null = null;
  let lastResponse: Response | undefined;

  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const response = await fetchWithTimeout(url, {
        ...fetchOptions,
        timeout,
      });

      // If response is ok or not retryable, return it
      if (response.ok || !isRetryableError(lastError || new Error(), response)) {
        return response;
      }

      lastResponse = response;
      lastError = new Error(`HTTP ${response.status}: ${response.statusText}`);

      // Don't retry on last attempt
      if (attempt === retries) {
        break;
      }

      // Calculate backoff delay
      const delay = getBackoffDelay(attempt, retryDelay);
      console.log(`[FetchRetry] Attempt ${attempt + 1}/${retries + 1} failed, retrying in ${Math.round(delay)}ms...`);
      
      if (onRetry) {
        onRetry(attempt + 1, lastError);
      }

      await new Promise(resolve => setTimeout(resolve, delay));

    } catch (error: any) {
      lastError = error;

      // Don't retry on last attempt
      if (attempt === retries) {
        break;
      }

      // Check if error is retryable
      if (!isRetryableError(error)) {
        throw error;
      }

      const delay = getBackoffDelay(attempt, retryDelay);
      console.log(`[FetchRetry] Attempt ${attempt + 1}/${retries + 1} failed: ${error.message}, retrying in ${Math.round(delay)}ms...`);
      
      if (onRetry) {
        onRetry(attempt + 1, error);
      }

      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }

  // All retries exhausted
  if (lastResponse) {
    return lastResponse;
  }

  throw lastError || new Error('Request failed after all retries');
}

/**
 * Helper to check if user is online before making request
 */
export function checkOnlineStatus(): boolean {
  return navigator.onLine;
}

/**
 * Wrapper for JSON API requests with retry logic
 */
export async function fetchJSON<T = any>(
  url: string,
  options: FetchWithRetryOptions = {}
): Promise<T> {
  const response = await fetchWithRetry(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    throw new Error(`HTTP ${response.status}: ${errorText}`);
  }

  return response.json();
}

export default fetchWithRetry;
